import { ButtonBase, Card, Stack, Typography, alpha, darken, lighten } from '@mui/material';
import { Genres, ProductGroups, Routes } from 'enums';
import _ from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { ProductMappedColors, Shadows } from 'theme';
import { makeStyles } from 'tss-react/mui';
import { formatDateTime } from 'utils/date.utils';
import { Project } from './text-generation-project-overview';

interface Props {
  project: Project;
  genre: Genres;
  itemsPerPage: number;
  productGroup?: ProductGroups;

  handleOpenEditDialog(project: Project): () => void;

  handleOpenConfirmDeleteDialog(project: Project): () => void;
}

const TextGenerationProjectItem: React.FC<Props> = ({
  project,
  genre,
  itemsPerPage,
  productGroup,
  handleOpenEditDialog,
  handleOpenConfirmDeleteDialog,
}) => {
  const { classes, cx } = useStyles({ itemsPerPage, productGroup, disableAW: _.isNil(project.profile) });
  const { t } = useTranslation();
  const navigate = useNavigate();

  const whiteProductLogo = '/dashboard/images/logos/product_logo_white.svg';

  const leadBoosterGenres = [
    Genres.CONNECTION_REQUEST,
    Genres.SOCIAL_POST,
    Genres.SOCIAL_AD,
    Genres.INVITATION,
    Genres.SALES_EMAIL,
    Genres.MARKETING_NEWSLETTER,
    Genres.PRODUCT_DESCRIPTION,
    Genres.BLOG_POST,
    Genres.LANDING_PAGE,
  ];

  const hrBoosterGenres = [Genres.JOB_OFFER];

  const isLeadBooster = () => {
    return !_.isNil(genre) && leadBoosterGenres.includes(genre);
  };

  const isHrBooster = () => {
    return !_.isNil(genre) && hrBoosterGenres.includes(genre);
  };

  const page = isLeadBooster() ? Routes.LEAD_BOOSTER_OPTIMIZE : isHrBooster() ? Routes.HR_BOOSTER_OPTIMIZE : Routes.HR_BOOSTER_OPTIMIZE;

  return (
    <Card className={classes.projectCard} elevation={0} square={true} onClick={() => navigate(`${page}?project=${project._id}`)}>
      <div className={classes.projectCardContent}>
        <div className={classes.arrow} />
        <img className={classes.logo} src={whiteProductLogo} alt="product logo" />

        <Typography className={classes.projectDate}>{formatDateTime(project.createDate, 'P')}</Typography>
        <Typography className={classes.projectName}>{project.projectName}</Typography>
        <Typography className={cx(classes.instruction, 'card-instruction')}>{t('selectProject')}</Typography>
      </div>
      <Stack gap={1} direction={'row'}>
        <ButtonBase
          className={classes.button}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            handleOpenConfirmDeleteDialog(project)();
          }}
        >
          {t('delete')}
        </ButtonBase>
        <ButtonBase
          className={classes.button}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            handleOpenEditDialog(project)();
          }}
        >
          {t('edit')}
        </ButtonBase>
      </Stack>
    </Card>
  );
};

const useStyles = makeStyles<{ itemsPerPage: number; productGroup?: ProductGroups; disableAW: boolean }>()(
  (theme, { itemsPerPage, productGroup, disableAW }) => ({
    projectCard: {
      pointerEvents: disableAW ? 'none' : 'all',
      flexBasis: '100%',
      maxWidth: `calc((100% / ${itemsPerPage}) - (${itemsPerPage - 1} / ${itemsPerPage} * 16px))`,
      background: productGroup ? ProductMappedColors[productGroup] : theme.palette.primary.main,
      boxShadow: Shadows.GREY_SHADOW_LIGHT,
      cursor: 'pointer',
      transition: 'transform 0.3s ease-in-out',
      '&:hover': {
        transform: 'scale(1.05)',
        boxShadow: Shadows.GREY_SHADOW_LIGHT,
      },
      [theme.breakpoints.down('lg')]: {
        cursor: 'pointer',
        transition: 'none',
        '&:hover': {
          transform: 'none',
        },
      },
    },
    projectCardContent: {
      position: 'relative',
      padding: '36px 16px 32px 32px',
      '&:hover': {
        '& .card-instruction': {
          visibility: 'visible',
        },
      },
    },
    projectDate: {
      color: '#FFF',
      fontSize: theme.typography.pxToRem(15),
      marginBottom: '24px',
    },
    projectName: {
      color: '#FFF',
      fontWeight: 600,
      fontSize: theme.typography.pxToRem(18),
      marginBottom: '4px',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
      overflow: 'hidden',
    },
    instruction: {
      visibility: 'hidden',
      position: 'absolute',
      whiteSpace: 'nowrap',
      bottom: 0,
      left: '50%',
      transform: 'translate(-50%, -50%)',
      fontSize: theme.typography.pxToRem(12),
      color: '#FFF',
    },
    arrow: {
      position: 'absolute',
      top: 'calc(32px - (40px / 2) + (30px / 2))',
      left: 0,
      width: 0,
      height: 0,
      borderTop: '20px solid transparent',
      borderBottom: '20px solid transparent',
      borderLeft: `20px solid ${alpha('#FFF', 0.8)}`,
    },
    logo: {
      position: 'absolute',
      top: 'calc(32px - (48px / 2) + (30px / 2))',
      right: '16px',
      width: '48px',
      height: '48px',
    },
    button: {
      pointerEvents: 'all',
      background: darken(productGroup ? ProductMappedColors[productGroup] : theme.palette.primary.main, 0.15),
      padding: '8px 16px',
      width: '100%',
      color: '#FFF',
      fontSize: theme.typography.pxToRem(14),
      fontWeight: 600,
      '&:hover': {
        background: lighten(productGroup ? ProductMappedColors[productGroup] : theme.palette.primary.main, 0.25),
      },
    },
  })
);

export default TextGenerationProjectItem;
